import type { QueryClient } from "@tanstack/react-query";
import { BASE_API_URL_V2 } from "@/constants";
import fpFetch from "@/services/fpFetch";

/** The unlock the viewer's password panel performs (#11856). The endpoint
 *  stores the password on the document, clears its locked flag and re-queues
 *  processing; from then on the preview endpoint serves the document
 *  decrypted. The panel knows the document, so the endpoint is addressed by
 *  document id. */
export type UnlockOutcome = { ok: true } | { ok: false; reason: string };

const FAILED_REASON_FALLBACK = "Password failed.";

export const unlockDocument = async (
  fileId: string,
  password: string
): Promise<UnlockOutcome> => {
  const url = new URL(`${BASE_API_URL_V2}/api/v2/tasks/unlock`);
  url.searchParams.set("fileId", fileId);
  url.searchParams.set("password", password);

  const response = await fpFetch(url.toString(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  if (response.ok) return { ok: true };

  // A rejection carries a reason ({detail}); anything else (a crash, a 403)
  // has none worth showing.
  let reason = FAILED_REASON_FALLBACK;
  try {
    const body: unknown = await response.json();
    if (
      typeof body === "object" &&
      body !== null &&
      "detail" in body &&
      typeof body.detail === "string"
    ) {
      reason = body.detail;
    }
  } catch {
    // Not JSON: keep the fallback.
  }
  return { ok: false, reason };
};

/** The preview endpoint now serves the unlocked document decrypted, and the
 *  viewer reads previews through react-query: a viewer that is open refetches,
 *  and every preview nobody is looking at is evicted so a later open starts
 *  from a fetch rather than from the cached locked bytes (invalidation alone
 *  would hand those back first). The caller knows only the task or the
 *  document, so the whole preview family is covered: one refetch per open
 *  viewer, and the next open of any other document fetches instead of reading
 *  its cached copy. */
export const dropCachedPreviews = (queryClient: QueryClient): void => {
  queryClient.removeQueries({ queryKey: ["fp0065"], type: "inactive" });
  void queryClient.invalidateQueries({ queryKey: ["fp0065"] });
};
